import type uPlot from "uplot";

// Shared wheel-zoom plugin for uPlot charts.
//
// Scroll → zoom around the cursor's data position (sample under the
// pointer stays put). Shift+scroll → pan 10% of the visible range.
// Double-click → reset to full data extent. Y-scales stay in auto so
// the visible range refits each time the x-window changes.
//
// onChange fires after every zoom/pan/reset with the values WE just
// passed to setScale — never read back u.scales.x because uPlot defers
// scale commits to the next paint and a same-tick read returns stale
// values, which the syncRange feedback loop then re-applies as a revert.

export interface WheelZoomOpts {
  factor?: number;
  onChange?: (range: [number, number]) => void;
}

export function wheelZoomPlugin(opts: WheelZoomOpts = {}): uPlot.Plugin {
  const factor = opts.factor ?? 0.85;
  const onChange = opts.onChange;

  return {
    hooks: {
      ready: (u) => {
        const over = u.over;

        const apply = (newMin: number, newMax: number) => {
          u.batch(() => {
            u.setScale("x", { min: newMin, max: newMax });
          });
          onChange?.([newMin, newMax]);
        };

        const onWheel = (e: WheelEvent) => {
          e.preventDefault();
          const xMin = u.scales.x.min;
          const xMax = u.scales.x.max;
          if (xMin == null || xMax == null) return;
          const data0 = u.data[0] as number[];
          if (!data0.length) return;
          const dataMin = data0[0];
          const dataMax = data0[data0.length - 1];
          const xRange = xMax - xMin;

          if (e.shiftKey) {
            const dir = e.deltaY > 0 ? 1 : -1;
            const step = xRange * 0.1 * dir;
            const newMin = Math.max(dataMin, xMin + step);
            const newMax = Math.min(dataMax, xMax + step);
            if (newMax - newMin > xRange * 0.5) {
              apply(newMin, newMax);
            }
            return;
          }

          const cursorLeft = u.cursor.left ?? u.bbox.width / 2;
          const cursorVal = u.posToVal(cursorLeft, "x");
          const newRange = e.deltaY < 0 ? xRange * factor : xRange / factor;
          const leftPct = (cursorVal - xMin) / xRange;
          let newMin = cursorVal - leftPct * newRange;
          let newMax = newMin + newRange;

          if (newMin < dataMin) {
            newMax += dataMin - newMin;
            newMin = dataMin;
          }
          if (newMax > dataMax) {
            newMin -= newMax - dataMax;
            newMax = dataMax;
          }
          newMin = Math.max(newMin, dataMin);
          newMax = Math.min(newMax, dataMax);

          const fullRange = dataMax - dataMin;
          if (newMax - newMin < fullRange * 0.001) return;

          apply(newMin, newMax);
        };

        const onDblClick = () => {
          const data0 = u.data[0] as number[];
          if (!data0.length) return;
          apply(data0[0], data0[data0.length - 1]);
        };

        // Click-drag to zoom into an x-range. Handled with our own mouse
        // listeners (not uPlot's cursor.drag) so the result routes through
        // apply() — the same setScale + onChange path as wheel zoom — and
        // cross-chart sync works identically. u.setSelect just paints the
        // rubber-band box; `false` = don't fire uPlot's setSelect hooks.
        const DRAG_MIN_PX = 6;  // smaller than this = a click, not a drag
        let dragStartX: number | null = null;
        const clearBox = () =>
          u.setSelect({ left: 0, top: 0, width: 0, height: 0 }, false);
        const onDown = (e: MouseEvent) => {
          if (e.button !== 0) return;
          dragStartX = e.offsetX;
        };
        const onMove = (e: MouseEvent) => {
          if (dragStartX == null) return;
          u.setSelect({
            left: Math.min(dragStartX, e.offsetX),
            top: 0,
            width: Math.abs(e.offsetX - dragStartX),
            height: u.bbox.height,
          }, false);
        };
        const onUp = (e: MouseEvent) => {
          if (dragStartX == null) return;
          const startX = dragStartX;
          dragStartX = null;
          clearBox();
          if (Math.abs(e.offsetX - startX) < DRAG_MIN_PX) return;  // a click
          const lo = u.posToVal(Math.min(startX, e.offsetX), "x");
          const hi = u.posToVal(Math.max(startX, e.offsetX), "x");
          if (hi > lo) apply(lo, hi);
        };
        const onLeave = () => {
          // Drag left the plot area — cancel rather than zoom to a guess.
          if (dragStartX != null) {
            dragStartX = null;
            clearBox();
          }
        };

        over.addEventListener("wheel", onWheel, { passive: false });
        over.addEventListener("dblclick", onDblClick);
        over.addEventListener("mousedown", onDown);
        over.addEventListener("mousemove", onMove);
        over.addEventListener("mouseup", onUp);
        over.addEventListener("mouseleave", onLeave);
      },
    },
  };
}

// Apply an externally-controlled x-range to a uPlot instance imperatively.
// Bails when the range already matches (approximate equality) to avoid
// the two-chart ping-pong loop when peer charts share zoom state.
export function applySyncRange(
  u: uPlot | null,
  syncRange: [number, number] | null
): void {
  if (!u || !syncRange) return;
  const cur = u.scales.x;
  const epsilon = Math.abs((syncRange[1] - syncRange[0]) * 1e-6);
  if (
    cur.min != null && cur.max != null &&
    Math.abs(cur.min - syncRange[0]) < epsilon &&
    Math.abs(cur.max - syncRange[1]) < epsilon
  ) return;
  u.batch(() => {
    u.setScale("x", { min: syncRange[0], max: syncRange[1] });
  });
}
